import React from "react";

const StoreBuddyPrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-40">
      <h1 className="text-3xl font-bold mb-6">
        Privacy Policy
      </h1>

      <h2 className="text-2xl font-semibold mb-4">
        Store Buddy — AI Shopping Assistant for Shopify
      </h2>

      <p className="mb-2">
        <strong>Effective Date:</strong> August 19, 2026
      </p>

      <p className="mb-2">
        <strong>Provider:</strong> Mean3
      </p>

      <p className="mb-6">
        <strong>Contact:</strong>{" "}
        <a
          href="mailto:Support@mean3.com"
          className="text-blue-500 hover:underline"
        >
          Support@mean3.com
        </a>
      </p>

      <p className="mb-6">
        <strong>In short:</strong> Store Buddy uses merchant, store, shopper,
        conversation, product, cart, and order-related data only to operate,
        secure, support, and improve the AI shopping assistant and its
        analytics. Mean3 does not sell personal information or use shopper
        conversations for independent advertising.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        1. About this Privacy Policy
      </h2>

      <p className="mb-4">
        This Privacy Policy explains how Mean3 ("Mean3," "we," "us," or "our")
        collects, uses, discloses, retains, and protects personal information
        when a Shopify merchant installs or uses Store Buddy (the "App"), when
        a shopper interacts with the Store Buddy storefront widget, or when
        someone contacts us for support.
      </p>

      <p className="mb-6">
        Store Buddy provides an AI shopping assistant, product
        recommendations, checkout assistance, conversation records, analytics,
        order attribution, customization controls, and merchant support
        features. This Policy applies to information processed through those
        services. A merchant's own privacy policy continues to govern the
        merchant's independent collection and use of shopper information.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        2. Our Role and the Merchant’s Role
      </h2>

      <p className="mb-4">
        For merchant account, billing, support, security, and service
        administration information, Mean3 generally determines why and how
        the information is used.
      </p>

      <p className="mb-4">
        For shopper information processed through Store Buddy on behalf of a
        merchant—such as chat messages, product interactions, cart activity,
        order-status requests, and attributed purchases—the merchant
        generally determines the purpose of the processing and Mean3 acts as
        its service provider or processor, as applicable.
      </p>

      <p className="mb-6">
        Shoppers should contact the relevant Shopify merchant first to
        exercise rights concerning store transactions or Store Buddy
        conversations. We will assist merchants with verified privacy
        requests as required by applicable law and Shopify's mandatory
        privacy processes.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        3. Information We Collect
      </h2>

      <h3 className="text-xl font-semibold mb-2">
        3.1 Information from merchants and Shopify
      </h3>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          Merchant and store details, such as store name, shop domain, Shopify
          shop identifier, business contact details, locale, currency, time
          zone, and app installation status.
        </li>
        <li>
          App authorization and technical information, including access
          tokens, granted permissions, session information, webhook events,
          and installation or uninstallation records.
        </li>
        <li>
          Catalog information needed for product discovery and
          recommendations, such as product titles, descriptions, variants,
          prices, inventory or availability, images, collections, and product
          links.
        </li>
        <li>
          Order, cart, and customer information permitted by the merchant and
          Shopify, when needed for order-status assistance, checkout support,
          conversion measurement, order attribution, complaint handling, or
          related App features. Depending on configuration and approved
          access, this may include an order identifier, order status, cart
          contents, purchase amount, customer identifier, and contact or
          delivery details.
        </li>
        <li>
          Merchant configuration, including assistant name, welcome message,
          brand colors, fonts, launcher settings, display rules, business
          hours, language settings, support preferences, feature toggles, and
          plan information.
        </li>
        <li>
          Support information, including the merchant's name, email address,
          subject, message, attachments if provided, and communications with
          Mean3 or Live Support.
        </li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">
        3.2 Information from shoppers and storefront use
      </h3>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          Conversation content, including questions, prompts, replies,
          feedback, complaint details, and information a shopper voluntarily
          enters into the widget.
        </li>
        <li>
          Shopping interactions, such as products shown or viewed,
          recommendations selected, links clicked, items added to cart,
          checkout progress, purchases, and revenue attributed to an
          AI-assisted session.
        </li>
        <li>
          Session and device information, such as session identifiers,
          timestamps, storefront page, browser or device type, language,
          approximate location inferred from IP address, IP address, and
          diagnostic logs, where collected for operation, security, analytics,
          or troubleshooting.
        </li>
        <li>
          Order lookup information a shopper submits or that Shopify makes
          available to complete an authorized order-status request.
        </li>
      </ul>

      <p className="mb-6">
        Please do not enter payment-card data, passwords, government
        identification numbers, health information, or other highly sensitive
        information into the chat. Store Buddy is not designed to request or
        process such information through ordinary conversations.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        3.3 Information collected automatically
      </h3>

      <p className="mb-6">
        We may automatically collect service logs, feature usage, error
        reports, performance data, authentication events, and security
        signals. We use this information to keep the App available, detect
        misuse, resolve failures, and understand how features perform.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        4. How We Use Information
      </h2>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          Provide and operate Store Buddy, including answering shopper
          questions, retrieving relevant catalog information, recommending
          products, supporting cart or checkout actions, and enabling human
          escalation.
        </li>
        <li>
          Provide merchant dashboards, conversation transcripts, session
          funnels, activity timelines, AI performance metrics, revenue
          attribution, widget health checks, and related reporting.
        </li>
        <li>
          Save and apply merchant settings, administer plans and usage limits,
          and communicate service or account notices.
        </li>
        <li>
          Respond to merchant and shopper support requests, complaints,
          troubleshooting needs, and security incidents.
        </li>
        <li>
          Maintain, test, debug, and improve the reliability, relevance,
          safety, and usability of the App. Where practical, we use aggregated
          or de-identified information for product analysis.
        </li>
        <li>
          Prevent fraud, abuse, unauthorized access, and violations of our
          terms; comply with law, lawful requests, and Shopify platform
          requirements; and establish or defend legal claims.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        5. AI Processing
      </h2>

      <p className="mb-4">
        Store Buddy uses artificial intelligence to interpret shopper
        messages and generate responses or product recommendations. To provide
        this function, relevant conversation text, store instructions, and
        selected catalog information may be sent to AI model or
        infrastructure providers acting for us.
      </p>

      <p className="mb-4">
        We limit the information sent to what is reasonably necessary for the
        requested function and contractually or technically restrict service
        providers from using it for unrelated purposes where available.
      </p>

      <p className="mb-4">
        AI responses can be incomplete or inaccurate. Merchants should
        maintain accurate product and policy information, and shoppers should
        confirm important details—especially price, availability, delivery,
        returns, and order status—before acting.
      </p>

      <p className="mb-6">
        Human support may review conversations that are escalated, reported,
        or needed for troubleshooting and safety.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        6. Legal Bases for Processing
      </h2>

      <p className="mb-4">
        Where the GDPR, UK GDPR, or similar law applies, we rely on one or
        more of the following legal bases:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          <strong>Contract:</strong> processing necessary to provide the App
          and requested services to a merchant.
        </li>
        <li>
          <strong>Legitimate interests:</strong> operating, securing,
          supporting, measuring, and improving Store Buddy; preventing fraud;
          and communicating about the service, balanced against individual
          rights.
        </li>
        <li>
          <strong>Consent:</strong> where consent is required for a particular
          cookie, tracking technology, communication, or optional processing
          activity.
        </li>
        <li>
          <strong>Legal obligation:</strong> processing necessary to comply
          with applicable laws, regulatory requests, tax or accounting duties,
          and Shopify privacy requirements.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        7. How We Share Information
      </h2>

      <p className="mb-4">
        We may disclose information only as needed to the following categories
        of recipients:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          The relevant merchant, including authorized staff who can access
          dashboards, transcripts, analytics, and escalated conversations.
        </li>
        <li>
          Shopify, where information must pass through Shopify APIs, webhooks,
          checkout, themes, or other platform services.
        </li>
        <li>
          Service providers that help us host, secure, monitor, email,
          support, analyze, and operate the App, including AI model and cloud
          infrastructure providers. They may process information only for
          contracted services and subject to appropriate confidentiality and
          security obligations.
        </li>
        <li>
          Professional advisers, auditors, insurers, regulators, law
          enforcement, courts, or other parties when reasonably necessary to
          comply with law, protect rights and safety, investigate abuse, or
          defend claims.
        </li>
        <li>
          A buyer, successor, or relevant participant in a merger, financing,
          acquisition, reorganization, bankruptcy, or sale of all or part of
          our business, subject to appropriate safeguards.
        </li>
      </ul>

      <p className="mb-4">
        We do not sell personal information for money.
      </p>

      <p className="mb-6">
        We also do not share personal information for cross-context
        behavioral advertising or use Store Buddy shopper data to build
        advertising profiles unrelated to the merchant's store.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        8. Cookies and Similar Technologies
      </h2>

      <p className="mb-4">
        Store Buddy may use necessary local storage, cookies, or similar
        technologies to maintain a chat session, remember widget state,
        support security, attribute an AI-assisted journey, and measure App
        performance.
      </p>

      <p className="mb-6">
        Merchants are responsible for configuring their storefront privacy
        disclosures and consent tools as required by law. Where Shopify's
        Customer Privacy API or another consent signal applies, the App should
        be configured to respect the applicable permission state for
        non-essential processing.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        9. Data Retention
      </h2>

      <p className="mb-4">
        We retain information only for as long as reasonably necessary for the
        purposes described in this Policy, including while the merchant uses
        the App and for a limited period afterward to provide support, meet
        legal or accounting obligations, resolve disputes, maintain security,
        and enforce agreements.
      </p>

      <p className="mb-4">
        Retention depends on the type of information, the merchant's settings
        or instructions, the sensitivity of the data, legal requirements, and
        operational need.
      </p>

      <p className="mb-4">
        When a merchant uninstalls Store Buddy or submits a valid deletion
        request, we delete or de-identify covered store and shopper
        information in accordance with Shopify's mandatory compliance
        webhooks and applicable law, unless continued retention is legally
        required or necessary to establish, exercise, or defend legal claims.
      </p>

      <p className="mb-6">
        Backups may retain data for a limited period until overwritten through
        normal cycles and remain protected from ordinary use.
      </p>

      <h2 className="text-2xl font-semibold mb-4">10. Security</h2>

      <p className="mb-4">
        We use reasonable administrative, technical, and organizational
        safeguards designed to protect personal information, including access
        controls, authentication, encryption in transit where supported,
        logging, restricted production access, and service-provider review.
      </p>

      <p className="mb-4">
        No system is completely secure, and we cannot guarantee absolute
        security.
      </p>

      <p className="mb-6">
        Merchants should limit staff access, protect credentials, and promptly
        notify us of suspected unauthorized use.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        11. International Data Transfers
      </h2>

      <p className="mb-6">
        Mean3 and its service providers may process information in the United
        States and other countries where we or they operate. These countries
        may have different data-protection laws. Where required, we use
        recognized transfer safeguards, such as contractual protections, and
        apply appropriate technical and organizational measures.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        12. Your Privacy Rights
      </h2>

      <p className="mb-4">
        Depending on location and applicable law, an individual may have rights
        to request access, correction, deletion, portability, restriction, or
        objection; withdraw consent; opt out of certain sale or sharing; or
        appeal a decision.
      </p>

      <p className="mb-4">
        Individuals may also have the right to complain to a data-protection
        authority. These rights are not absolute and may be subject to
        verification and legal exceptions.
      </p>

      <p className="mb-4">
        Merchant users may submit a request directly to Mean3. Shoppers should
        normally contact the Shopify merchant whose store they used because
        the merchant controls the relevant customer relationship.
      </p>

      <p className="mb-4">
        Merchants can forward verified requests to us, and we will support
        them through Shopify's required privacy processes.
      </p>

      <p className="mb-6">
        We will not discriminate against an individual for exercising an
        applicable privacy right.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        13. California Privacy Notice
      </h2>

      <p className="mb-4">
        If the California Consumer Privacy Act, as amended, applies, the
        categories of personal information we may have collected in the
        preceding 12 months include identifiers; customer records and
        commercial information; internet or electronic activity; approximate
        geolocation; professional or employment information relating to
        merchant contacts; and inferences generated from shopping
        interactions.
      </p>

      <p className="mb-4">
        The exact categories depend on the merchant's configuration and the
        shopper's use of Store Buddy.
      </p>

      <p className="mb-4">
        We collect and use these categories for the business and commercial
        purposes described in Sections 4 and 5 and disclose them to the
        recipient categories in Section 7.
      </p>

      <p className="mb-4">
        We do not knowingly sell personal information or share it for
        cross-context behavioral advertising.
      </p>

      <p className="mb-6">
        We do not knowingly collect or use sensitive personal information to
        infer characteristics about individuals.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        14. Children’s Privacy
      </h2>

      <p className="mb-4">
        Store Buddy is a business service for Shopify merchants and is not
        directed to children under 13.
      </p>

      <p className="mb-4">
        We do not knowingly collect personal information directly from
        children under 13 through the App.
      </p>

      <p className="mb-6">
        If you believe a child has provided personal information, contact us
        so we can investigate and take appropriate action. Merchants remain
        responsible for age-related requirements applicable to their stores
        and products.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        15. Merchant Responsibilities
      </h2>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          Provide shoppers with an accurate privacy notice explaining the
          merchant's use of Store Buddy and other store technologies.
        </li>
        <li>
          Obtain any consent required for cookies, tracking, AI-assisted
          processing, or marketing, and honor applicable opt-out signals.
        </li>
        <li>
          Request only the Shopify permissions and Store Buddy features
          reasonably needed for the merchant's use case.
        </li>
        <li>
          Keep product, policy, contact, and order information accurate;
          control authorized staff access; and avoid placing prohibited or
          highly sensitive information in chat instructions or transcripts.
        </li>
        <li>
          Respond to shopper privacy requests and notify Mean3 when our
          assistance is needed.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        16. Third-Party Services and Links
      </h2>

      <p className="mb-6">
        The App may link to Shopify, merchant storefront pages, products,
        checkout, or other third-party services. Their privacy practices are
        governed by their own notices. This Policy does not cover information
        a merchant or third party collects independently outside Store Buddy.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        17. Changes to this Policy
      </h2>

      <p className="mb-4">
        We may update this Policy to reflect changes to Store Buddy, our
        service providers, legal requirements, or data practices.
      </p>

      <p className="mb-4">
        We will post the revised version and update the effective date.
      </p>

      <p className="mb-6">
        If a change materially affects how we use personal information, we will
        provide additional notice where required.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        18. Contact Us
      </h2>

      <p className="mb-4">
        For questions, privacy requests, or complaints about Store Buddy,
        contact:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          <strong>Mean3</strong>
        </li>
        <li>
          <strong>Email:</strong>{" "}
          <a
            href="mailto:Support@mean3.com"
            className="text-blue-500 hover:underline"
          >
            Support@mean3.com
          </a>
        </li>
        <li>
          <strong>Address:</strong> 447 Broadway, 2nd Floor, Suite #670, New
          York, NY 10013, United States
        </li>
      </ul>
      

      <p className="mb-4">
        To help us locate the relevant information, include the Shopify store
        domain, your relationship to the store, the nature of the request, and
        any relevant conversation or order reference. Do not email passwords or full payment-card details.
      </p>
    </div>
  );
};

export default StoreBuddyPrivacyPolicy;